import { type JSX, useCallback } from 'react';

import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { PageContent } from '@/components';
import { DashboardGrid } from '@/features/performance-dashboard/components/DashboardGrid/DashboardGrid';
import {
  type PerformanceDashboardUrlFilters,
  performanceDashboardFiltersSchema,
} from '@/features/performance-dashboard/lib/performanceDashboardFilters.schema';
import { useResultsContext } from '@/features/performance-dashboard/pages/Resultados/ResultContext';
import { ResultsHeader } from '@/features/performance-dashboard/pages/Resultados/ResultHeader';
import { ResultsProvider } from '@/features/performance-dashboard/pages/Resultados/ResultProvider';
import { requireAuthenticated } from '@/permissions';
import '../styles.scss';

export const Route = createFileRoute('/resultados/')({
  staticData: { title: 'Resultados' },
  beforeLoad: requireAuthenticated(),
  validateSearch: (search: Record<string, unknown>): PerformanceDashboardUrlFilters =>
    performanceDashboardFiltersSchema.parse(search),
  component: ResultadosRoute,
});

function ResultsGrid(): JSX.Element {
  const { filters, dashboardResults, isLoading } = useResultsContext();
  return (
    <DashboardGrid
      filters={filters}
      dashboardResults={dashboardResults}
      isLoading={isLoading}
    />
  );
}

function ResultadosRoute(): JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const handleNavigate = useCallback(
    ({ search: nextSearch, replace }: { search: PerformanceDashboardUrlFilters; replace?: boolean }) => {
      void navigate({ search: nextSearch, replace });
    },
    [navigate]
  );

  return (
    <ResultsProvider
      search={search}
      navigate={handleNavigate}>
      <ResultsHeader />
      <PageContent>
        <ResultsGrid />
      </PageContent>
    </ResultsProvider>
  );
}
